import React, { useState, useEffect, useCallback } from 'react';

/**
 * Returns the upcoming Sunday at 23:59:59.
 * If today is Sunday, returns THIS Sunday (end of today).
 */
function getNextSunday() {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const daysUntilSunday = day === 0 ? 0 : 7 - day;
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntilSunday);
  target.setHours(23, 59, 59, 0);
  return target;
}

/**
 * Format a Date as "DD/MM" (e.g. "16/03")
 */
function formatDateShort(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

/**
 * Auto-rolling countdown timer.
 * - Always counts down to the coming Sunday at 23:59:59
 * - When it hits zero, automatically resets to the next Sunday
 * - Exposes `endDateFormatted` so the parent can display the date
 */
const Countdown = ({ onDateChange }) => {
  const [targetDate, setTargetDate] = useState(getNextSunday);

  const calculateTimeLeft = useCallback(() => {
    const difference = targetDate - new Date();

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  // Tick every second; auto-reset when countdown expires
  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      // If expired, roll to next Sunday
      if (remaining.days === 0 && remaining.hours === 0 && remaining.minutes === 0 && remaining.seconds === 0) {
        const next = getNextSunday();
        // If getNextSunday returns today (which just expired), push 7 days
        if (next <= new Date()) {
          next.setDate(next.getDate() + 7);
        }
        setTargetDate(next);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  // Notify parent of date changes
  useEffect(() => {
    if (onDateChange) onDateChange(formatDateShort(targetDate));
  }, [targetDate, onDateChange]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '11px', opacity: 0.85, marginRight: '4px', whiteSpace: 'nowrap' }}>
        t.o.m. {formatDateShort(targetDate)}
      </span>
      {[
        { value: timeLeft.days, label: 'dagar' },
        { value: timeLeft.hours, label: 'tim' },
        { value: timeLeft.minutes, label: 'min' },
        { value: timeLeft.seconds, label: 'sek' },
      ].map((unit, i) => (
        <React.Fragment key={unit.label}>
          {i > 0 && <span style={{ fontSize: '16px', fontWeight: 700, marginTop: '-8px' }}>:</span>}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {pad(unit.value)}
            </span>
            <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>
              {unit.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default Countdown;
